import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import {
  SuperModelDetailResponse,
  SuperModelForm,
  SuperModelListResponse,
  SuperModelManufacturerFilterOptionsResponse,
  SuperModelUpdate,
} from "../types";
import {
  selectSuperModelForm,
  selectSuperModelFormDropDown,
  selectSuperModelManufacturerFilter,
  selectSuperModelPage,
  selectSuperModelPerPage,
} from "./selector";
import { superModelActions } from "./slice";

let isFetchingList = false;
let isFetchingFormDropDown = false;

export function* handleGetSuperModelsList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectSuperModelPage);
    const perPage: number = yield select(selectSuperModelPerPage);
    const manufacturerFilter: string = yield select(
      selectSuperModelManufacturerFilter
    );

    let url = `${API_URL}/super-model/list?page=${page}&perPage=${perPage}`;

    if (manufacturerFilter !== "ALL") {
      url += `&manufacturer=${manufacturerFilter}`;
    }

    const response: SuperModelListResponse = yield call(request, url, {
      method: "GET",
    });

    const data = response?.data || [];
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { data, meta };

    if (page === 1) {
      yield put(superModelActions.getSuperModelsListSuccess(payload));
    } else {
      yield put(superModelActions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const page: number = yield select(selectSuperModelPage);

    if (page === 1) {
      yield put(superModelActions.getSuperModelsListError(message));
    } else {
      yield put(superModelActions.getNextPageItemsError(message));
    }
  } finally {
    isFetchingList = false;
  }
}

export function* handleAddSuperModel() {
  const form: SuperModelForm = yield select(selectSuperModelForm);

  try {
    const response = yield call(request, `${API_URL}/super-model`, {
      method: "POST",
      body: JSON.stringify({
        superModelName: form.name.trim(),
        manufacturerId: Number(form.manufacturer),
        priority: Number(form.priority),
      }),
    });

    yield put(superModelActions.addSuperModelSuccess(response.data));
  } catch (error) {
    yield put(
      superModelActions.addSuperModelError((error as Error).message)
    );
  }
}

export function* handleUpdateSuperModel(
  action: PayloadAction<SuperModelUpdate>
) {
  const { id, superModelName, manufacturerId, priority } = action.payload;

  try {
    yield call(request, `${API_URL}/super-model/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        superModelName: superModelName.trim(),
        manufacturerId: Number(manufacturerId),
        priority: Number(priority),
      }),
    });

    yield put(superModelActions.updateSuperModelSuccess());
    yield put(superModelActions.getSuperModelsList());
  } catch (error) {
    yield put(
      superModelActions.updateSuperModelError((error as Error).message)
    );
  }
}

export function* handleGetSuperModelDetail(action: PayloadAction<number>) {
  const id = action.payload;

  try {
    const response: SuperModelDetailResponse = yield call(
      request,
      `${API_URL}/super-model/${id}`,
      { method: "GET" }
    );

    yield put(superModelActions.getSuperModelDetailSuccess(response?.data));
  } catch (error) {
    yield put(
      superModelActions.getSuperModelDetailError((error as Error).message)
    );
  }
}

export function* handleGetManufacturerFilterOptions() {
  try {
    const response: SuperModelManufacturerFilterOptionsResponse = yield call(
      request,
      `${API_URL}/super-model/filter-options/manufacturers`,
      { method: "GET" }
    );

    yield put(
      superModelActions.getManufacturerFilterOptionsSuccess(
        Array.isArray(response?.data?.manufacturers)
          ? response.data.manufacturers
          : []
      )
    );
  } catch (error) {
    yield put(
      superModelActions.getManufacturerFilterOptionsError(
        (error as Error).message
      )
    );
  }
}

export function* handleGetFormDropDownList() {
  if (isFetchingFormDropDown) return;
  isFetchingFormDropDown = true;

  try {
    const formDropDown = yield select(selectSuperModelFormDropDown);
    const { page, perPage } = formDropDown;

    const response = yield call(
      request,
      `${API_URL}/manufacturer?page=${page}&perPage=${perPage}`,
      { method: "GET" },
    );

    const data = response?.data || [];
    const items = data
      .map((row: { menufecturerID: number; menufecturerName: string }) => ({
        id: row.menufecturerID,
        name: row.menufecturerName,
      }))
      .filter((item: { id: number; name: string }) => item.id && item.name);

    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { data: items, meta };

    if (page === 1) {
      yield put(superModelActions.getFormDropDownListSuccess(payload));
    } else {
      yield put(superModelActions.getFormDropDownNextPageSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const formDropDown = yield select(selectSuperModelFormDropDown);

    if (formDropDown.page === 1) {
      yield put(superModelActions.getFormDropDownListError(message));
    } else {
      yield put(superModelActions.getFormDropDownNextPageError(message));
    }
  } finally {
    isFetchingFormDropDown = false;
  }
}

export function* handleDeleteSuperModel(action: PayloadAction<number>) {
  const id = action.payload;

  try {
    yield call(request, `${API_URL}/super-model/${id}`, {
      method: "DELETE",
    });

    yield put(superModelActions.deleteSuperModelSuccess(id));
  } catch (error) {
    yield put(
      superModelActions.deleteSuperModelError((error as Error).message)
    );
  }
}

export function* superModelSaga() {
  yield takeLeading(
    superModelActions.getSuperModelsList.type,
    handleGetSuperModelsList
  );
  yield takeLeading(
    superModelActions.getNextPageItems.type,
    handleGetSuperModelsList
  );
  yield takeLatest(
    superModelActions.addSuperModel.type,
    handleAddSuperModel
  );
  yield takeLatest(
    superModelActions.updateSuperModel.type,
    handleUpdateSuperModel
  );
  yield takeLatest(
    superModelActions.getSuperModelDetail.type,
    handleGetSuperModelDetail
  );
  yield takeLatest(
    superModelActions.getManufacturerFilterOptions.type,
    handleGetManufacturerFilterOptions
  );
  yield takeLeading(
    superModelActions.getFormDropDownList.type,
    handleGetFormDropDownList,
  );
  yield takeLeading(
    superModelActions.getFormDropDownNextPage.type,
    handleGetFormDropDownList,
  );
  yield takeLatest(
    superModelActions.deleteSuperModel.type,
    handleDeleteSuperModel
  );
}

export default superModelSaga;
